import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { formatAge } from '@/utils/ageHelper';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteAppendix7Mutation,
	useGetAppendix7DetailQuery,
} from '@/api/forms-management/appendix7';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
	useGetAppendix7GenerateReportMutation,
	useGetExportCustomerPDFMutation,
} from '@/api/common';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function Appendix7DetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, appendix7Id, admissionRequestId } = useParams();
	const showModal = useModal();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [deleteAppendix7] = useDeleteAppendix7Mutation();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getAppendix7GenerateReport] = useGetAppendix7GenerateReportMutation();

	const { data: details }: any = useGetAppendix7DetailQuery(appendix7Id || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.appendix7ServiceAdmission'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteAppendix7(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	const formartReasons = () => {
		const arr = [];

		if (details?.data.interventionCompleted)
			arr.push(t('preAdmission.interventionCompleted'));
		if (details?.data.admittedToResidential)
			arr.push(t('preAdmission.admittedToResidential'));
		if (details?.data.admittedToPsychiatricHospital)
			arr.push(t('preAdmission.admittedToPsychiatricHospital'));
		if (details?.data.persistentlyUnmotivated)
			arr.push(t('preAdmission.persistentlyUnmotivated'));
		if (details?.data.selfWithdrawal)
			arr.push(t('preAdmission.selfWithdrawal'));
		if (details?.data.untraceable) arr.push(t('preAdmission.untraceable'));
		if (details?.data.serviceTerminationOthers)
			arr.push(
				`${t('preAdmission.others')} (${
					details?.data.serviceTerminationOthersSpecify
				})`
			);

		return arr.join(';\n');
	};

	const formartServiceRendered = () => {
		const arr = [];

		if (details?.data.counsellingToServiceUser)
			arr.push(t('preAdmission.mentalSuspectedMentalProblems'));
		if (details?.data.counsellingToFamily)
			arr.push(t('preAdmission.memberCarerServiceUser'));
		if (details?.data.successfulEngagement)
			arr.push(
				t('preAdmission.supportDevelopmentalGroupsEducationalProgrammes')
			);
		if (details?.data.occupationalTherapy)
			arr.push(
				t('preAdmission.serviceUserReceiving') +
					' ' +
					t('preAdmission.occupationalTherapy')
			);
		if (details?.data.outreaching)
			arr.push(
				t('preAdmission.serviceUserReceiving') +
					' ' +
					t('preAdmission.outreaching')
			);
		if (details?.data.trainingServices)
			arr.push(
				t('preAdmission.serviceUserReceiving') +
					' ' +
					t('preAdmission.trainingServices')
			);
		if (details?.data.successfullyReferred)
			arr.push(t('preAdmission.successfullyReferred'));
		if (details?.data.peerSupportService)
			arr.push(t('preAdmission.peerSupportService'));
		if (details?.data.clinicalPsychologicalService)
			arr.push(t('preAdmission.clinicalPsychologicalService'));
		if (details?.data.others)
			arr.push(
				`${t('preAdmission.othersSpecify')} ${details?.data.othersSpecify}`
			);

		return arr.join(';\n');
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.appendix7Detail')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getAppendix7GenerateReport(
									details?.data.id
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.from')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.officerInCharge')}
								value={details?.data.fromOfficerInCharge}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.unit')}
								value={details?.data.branchName}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.ref')}
								value={details?.data.fromRefNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.telNo')}
								value={details?.data.fromTelNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.faxNo')}
								value={details?.data.fromFaxNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.date')}
								value={
									details?.data.fromDate
										? dayjs(details?.data.fromDate)
												.locale('en')
												.format(DISPLAY_DATE_FORMAT)
										: ''
								}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.to')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.officerInCharge')}
								value={details?.data.toOfficerInCharge}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.nameOfTheOrganisation')}
								value={details?.data.nameOfOrganization}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.ref')}
								value={details?.data.toRefNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.dated')}
								value={
									details?.data.toDated
										? dayjs(details?.data.toDated)
												.locale('en')
												.format(DISPLAY_DATE_FORMAT)
										: ''
								}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.faxNo')}
								value={details?.data.toFaxNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.totalPages')}
								value={details?.data.toTotalPages}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.receiptOfReferralForICCMW')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.name')}
								value={details?.data.nameEn}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.hkicNo')}
								value={details?.data.hkicNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.sex')}
								value={details?.data.sexDesc}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.age')}
								value={formatAge(details?.data.age, details?.data.ageEstimated)}
							/>
						</Grid>
						<Grid size={12}>
							<AppFieldView
								label={t(
									'preAdmission.referringAboveNamedServiceUserUouReferredOn'
								)}
								value={
									details?.data.referralDate
										? dayjs(details?.data.referralDate)
												.locale('en')
												.format(DISPLAY_DATE_FORMAT)
										: ''
								}
							/>
						</Grid>
						<Grid size={12}>
							<AppFieldView
								label={t('preAdmission.numServiceRendered')}
								value={formartServiceRendered()}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.numServiceExit')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.dateOfTermination')}
								value={
									details?.data.dateOfTermination
										? dayjs(details?.data.dateOfTermination)
												.locale('en')
												.format(DISPLAY_DATE_FORMAT)
										: ''
								}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Grid container spacing={3} marginTop={0}>
						<Grid size={12}>
							<AppFieldView
								label={t('preAdmission.reasons')}
								value={formartReasons()}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.numRemarks')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={12}>
							<AppFieldView
								label={t('preAdmission.serviceTerminationRemarks')}
								value={details?.data.serviceTerminationRemarks}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.shouldEnquiries')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.name')}
								value={details?.data.contactWorkerName}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.telNo')}
								value={details?.data.contactTel}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.officerInCharge')}
								value={details?.data.contactOfficerInCharge}
							/>
						</Grid>
					</Grid>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
